import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMedia } from "@/contexts/MediaContext";
import { useFrameStable } from "@/contexts/FrameContext";
import { Chip } from "@scripts/react/components/Chip";
import { FileUploadModal } from "@/components/file/UploadModal/FileUploadModal";
import { FileModal } from "./FileModal";
import { useFileCarouselThumbnails } from "@/hooks/useFileCarousel";
import { useCarouselNavigation } from "@/hooks/useCarouselNavigation";
import { ThumbnailContentWithProgress } from "@/components/file/Carousel/ThumbnailContentWithProgress";
import { MediaItemStatus, MediaItem } from "@/types/media";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useMediaStore } from "@/store";
import { useHasReachedUploadLimit } from "@/hooks/useUploadLimit";
import { TooltipContainer } from "@scripts/react/components/Tooltip";

interface FileGridPreviewProps {
    onAddFiles: (files: File[]) => Promise<void>;
    orderedMediaItems: MediaItem[];
    isHomePage?: boolean;
}

export const FileGridPreview = ({
    onAddFiles,
    orderedMediaItems,
    isHomePage = false
}: FileGridPreviewProps) => {
    const gridScrollRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null); // TODO: Update when mobile uploading is restored
    const { mediaItems, deleteMediaItem } = useMedia();
    const mediaStore = useMediaStore(); // Subscribe to store changes for progress updates
    const { orderedTimelineFramePreviewUrls } = useFrameStable();
    const prevUploadCountRef = useRef(mediaItems.length);
    const currentBlobUrlRef = useRef<string | null>(null);
    const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
    const [selectedFileModal, setSelectedFileModal] = useState<{
        fileId: string;
        fileUrl: string;
        fileName: string;
        isVideo: boolean;
    } | null>(null);

    const hasReachedUploadLimit = useHasReachedUploadLimit(orderedMediaItems);

    const isSingleFile = orderedMediaItems.length === 1;
    const thumbnails = useFileCarouselThumbnails(
        orderedMediaItems,
        orderedTimelineFramePreviewUrls,
        isSingleFile
    );

    // Scroll to bottom when new files are added
    useEffect(() => {
        if (mediaItems.length > prevUploadCountRef.current) {
            gridScrollRef.current?.scrollTo({
                top: gridScrollRef.current.scrollHeight,
                behavior: "smooth"
            });
        }
        prevUploadCountRef.current = mediaItems.length;
    }, [mediaItems.length]);

    const currentFileIndex = useMemo(() => {
        if (!selectedFileModal) return -1;
        return orderedMediaItems.findIndex((f) => f.id === selectedFileModal.fileId);
    }, [selectedFileModal, orderedMediaItems]);

    const handleNavigateToFile = useCallback(
        (file: any) => {
            if (currentBlobUrlRef.current) {
                URL.revokeObjectURL(currentBlobUrlRef.current);
            }
            const fileUrl = URL.createObjectURL(file.file);
            currentBlobUrlRef.current = fileUrl;
            setSelectedFileModal({
                fileId: file.id,
                fileUrl,
                fileName: file.file.name,
                isVideo: file.isVideo || false
            });
        },
        [setSelectedFileModal]
    );

    const { handlePrevious: handlePreviousFile, handleNext: handleNextFile } =
        useCarouselNavigation({
            items: orderedMediaItems,
            currentIndex: currentFileIndex,
            onNavigate: handleNavigateToFile
        });

    const handleThumbnailClick = useCallback(
        (fileId: string) => {
            const mediaItem = mediaItems.find((item) => item.id === fileId);
            if (mediaItem) {
                if (currentBlobUrlRef.current) {
                    URL.revokeObjectURL(currentBlobUrlRef.current);
                }
                const fileUrl = URL.createObjectURL(mediaItem.file);
                currentBlobUrlRef.current = fileUrl;
                setSelectedFileModal({
                    fileId,
                    fileUrl,
                    fileName: mediaItem.file.name,
                    isVideo: mediaItem.isVideo || false
                });
            }
        },
        [mediaItems]
    );

    const handleDelete = useCallback(
        (e: React.MouseEvent, fileId: string) => {
            e.stopPropagation();
            deleteMediaItem(fileId);
        },
        [deleteMediaItem]
    );

    // TODO: Update when mobile uploading is restored
    const onAddFilesClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileSelected = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || []);
            await onAddFiles(files);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        },
        [onAddFiles]
    );

    const handleCloseModal = useCallback(() => {
        if (currentBlobUrlRef.current) {
            URL.revokeObjectURL(currentBlobUrlRef.current);
            currentBlobUrlRef.current = null;
        }
        setSelectedFileModal(null);
    }, []);

    useEffect(() => {
        return () => {
            if (currentBlobUrlRef.current) {
                URL.revokeObjectURL(currentBlobUrlRef.current);
                currentBlobUrlRef.current = null;
            }
        };
    }, []);

    return (
        <>
            <div
                className={twClassNames(
                    "fileCarouselWindow flex h-full flex-col",
                    isHomePage && "!border-0"
                )}
            >
                <div className="fileCarouselHeader gap-2">
                    <div className="flex items-center gap-2">
                        <h3 className="title">Files</h3>
                        <Chip color="gray" type="number">
                            {orderedMediaItems.length}
                        </Chip>
                    </div>
                    {hasReachedUploadLimit ? (
                        // @ts-expect-error - TooltipContainer component type issue
                        <TooltipContainer
                            type="titleOnly"
                            placement="top"
                            interactive={true}
                            title={
                                <span>
                                    <strong>
                                        {orderedMediaItems.some((item) => item.isVideo)
                                            ? "You only need 1 video to build a model. "
                                            : "You only need 10 images to build a model. "}
                                    </strong>
                                    <br></br> To label a large amount of data, use our{" "}
                                    <a
                                        href="https://app.roboflow.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-purboflow-300 hover:text-purboflow-200"
                                    >
                                        core platform
                                    </a>
                                    .
                                </span>
                            }
                            customButton={
                                <Chip
                                    color="white"
                                    aria-label="Add files"
                                    className="cursor-not-allowed opacity-50"
                                >
                                    <i className="far fa-plus" />
                                    Add Files
                                </Chip>
                            }
                        />
                    ) : (
                        <Chip
                            color="white"
                            onClick={onAddFilesClick}
                            aria-label="Add files"
                            className="cursor-pointer hover:bg-gray-100"
                        >
                            <i className="far fa-plus" />
                            Add Files
                        </Chip>
                    )}
                </div>
                <div ref={gridScrollRef} className="flex-1 overflow-y-auto">
                    <div
                        className={
                            isSingleFile
                                ? "flex h-full p-3"
                                : "grid grid-cols-5 gap-3 p-3 md:grid-cols-6"
                        }
                    >
                        {orderedMediaItems.map((mediaItem, index) => {
                            const thumbnail = thumbnails[mediaItem.id];
                            const isUploading =
                                mediaItem.status === MediaItemStatus.UPLOADING ||
                                mediaItem.status === MediaItemStatus.PROCESSING;

                            return (
                                <div
                                    key={mediaItem.id}
                                    role="button"
                                    tabIndex={!isUploading ? 0 : -1}
                                    onKeyDown={(e) => {
                                        if (!isUploading && (e.key === "Enter" || e.key === " ")) {
                                            e.preventDefault();
                                            handleThumbnailClick(mediaItem.id);
                                        }
                                    }}
                                    className={isSingleFile ? "h-full w-full" : ""}
                                >
                                    <ThumbnailContentWithProgress
                                        mediaItem={mediaItem}
                                        thumbnail={thumbnail}
                                        isUploading={isUploading}
                                        fileType={mediaItem.fileType}
                                        fileName={
                                            mediaItem.file.name ||
                                            `${mediaItem.fileType} #${index + 1}`
                                        }
                                        index={index}
                                        className={isSingleFile ? "!h-full !w-full" : ""}
                                        title={
                                            isUploading
                                                ? `Processing ${mediaItem.fileType}...`
                                                : "Open in full screen"
                                        }
                                        onClick={
                                            !isUploading
                                                ? () => handleThumbnailClick(mediaItem.id)
                                                : undefined
                                        }
                                        onDelete={(evt) => handleDelete(evt, mediaItem.id)}
                                        isShareMode={false}
                                        flowLocked={isUploading}
                                        showOverlay={true}
                                        showPlayButton={
                                            isSingleFile && mediaItem.fileType === "video"
                                        }
                                        insideGrid={!isSingleFile}
                                        isSingleFile={isSingleFile}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* TODO: Update when mobile uploading is restored */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelected}
                className="hidden"
                aria-label="Select files to upload"
            />

            <FileUploadModal
                title="Add Files"
                isModalOpen={isFileUploadModalOpen}
                setIsModalOpen={setIsFileUploadModalOpen}
                onAddMediaItems={(files: File[]) => {
                    onAddFiles(files);
                    setIsFileUploadModalOpen(false);
                }}
            />

            <FileModal
                isOpen={!!selectedFileModal}
                fileUrl={selectedFileModal?.fileUrl || ""}
                fileName={selectedFileModal?.fileName || ""}
                isVideo={selectedFileModal?.isVideo || false}
                onClose={handleCloseModal}
                onPrevious={handlePreviousFile}
                onNext={handleNextFile}
            />
        </>
    );
};
